// @flow
import React, { useEffect, useMemo } from 'react';
import { useMuiGridServerSide } from "../../../../hooks/useMuiGridServerSide";
import { DataGridPremium, GRID_AGGREGATION_ROOT_FOOTER_ROW_ID, GridActionsCellItem } from '@mui/x-data-grid-premium';
import { useTranslation } from "react-i18next";
import { LOANS } from "../../locales/namespaces";
import {
	Card,
	CardContent,
	Tooltip,
	Typography,
	Box,
	Button,
	Grid,
	IconButton, useMediaQuery
} from "@mui/material";
import { Link, useHistory } from 'react-router-dom';
import { getRouteUrl } from "../../../../helpers/getRouteUrl";
import { ROUTE_PAGE_LOANS_DETAILS } from "../../routes/routes";
import DataGridWrapper from "../../../../components/dataGridWrapper/dataGridWrapper";
import PermissionHOC, { withPermissionHOC } from "../../../permission/components/permissionHOC/permissionHOC";
import Breadcrumb from "../../../../components/breadcrumb/breadcrumb";
import { ROUTE_PAGE_HOME, ROUTE_PAGE_WIZARD } from "../../../../routers/routes";
import useUserData from "../../../../hooks/user/useUserData";
import AlertMessageForSimpleUser
	from "../../../customers/components/alertMessageForSimpleUser/alertMessageForSimpleUser";
import VisibilityIcon from '@mui/icons-material/Visibility';
import moment from "moment";
import IndexListLoans from "../../components/indexList/indexListLoans";
import { muiGridLocales } from "../../../../helpers/muiGridLocales";
import MainCard from "../../../../theme/mantis/components/mainCard";
import { CustomPagination } from "../../../../components/dataGridCustom/customPagination";
import { textTranslate } from "../../../../helpers/helpers";
import { mapLoan } from "../../types/map";
import { NumericFormat } from "react-number-format";
import useLoanStatus from "../../../parameters/hooks/useLoanStatus";
import useLoanType from "../../../parameters/hooks/useLoanType";
import PlusIcon from "../../../../../src/assets/images/icons/buttonsIcons/plusIcon";
import { showPopup } from '../../../../components/globalPopup/globalPopupService';


const Index = () => {
	const { t } = useTranslation(LOANS)
	const history = useHistory();
	const url = `loan/indexTable/data`
	const { language, thousandSeparator, decimalSeparator } = useUserData()
	const matchDownSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));

	useEffect(() => {
		document.title = t('IMS Financial Consulting | Mortgages loans')
	}, [t])

	const {
		isLoading,
		data,
		total,
		page,
		pageSize,
		onPaginationModelChange,
		onSortModelChange,
		onFilterModelChange,
		refreshGrid
	} = useMuiGridServerSide({
		url,
		initialPageSize: 50,
		initialSortModel: [{ name: 'id', direction: 'descending' }],
		mapFunction: mapLoan
	})

	const { loanStatus } = useLoanStatus()
	const { loanType } = useLoanType()
	const activeLoans = data?.filter((loan) => loan?.ActiveStatus === "1") ?? []
	const hasActiveLoans = activeLoans?.length > 0

	const columns = useMemo(() => [
		{
			field: 'actions',
			headerName: t('Actions'),
			type: 'actions',
			getActions: (params) => {
				if (params.id === GRID_AGGREGATION_ROOT_FOOTER_ROW_ID) {
					return [];
				}
				return [
					<GridActionsCellItem
						component={Link}
						to={getRouteUrl(ROUTE_PAGE_LOANS_DETAILS, { id: params.id })}
						icon={
							<Tooltip title={t('View')}>
								<IconButton>
									<VisibilityIcon />
								</IconButton>
							</Tooltip>
						}
						label={t("Details")}>
					</GridActionsCellItem>
				]
			}
		},
		{
			field: 'StatusId',
			headerName: t('Loan status'),
			minWidth: 200, flex: 1,
			type: 'singleSelect',
			valueOptions: loanStatus.map(loanStatus => ({
				value: loanStatus.id,
				label: textTranslate(language)({ en: loanStatus.DescriptionEn, el: loanStatus.DescriptionEl }),
			})),
		},
		{
			field: 'Customer',
			headerName: t('Name'),
			minWidth: 250, flex: 1,
		},
		{
			field: 'PhoneNumber',
			headerName: t('Phone'),
			minWidth: 150, flex: 1,
		},
		{
			field: 'Email',
			headerName: t('Email'),
			minWidth: 250, flex: 1,
		},
		{
			field: 'TypeId',
			headerName: t('Type'),
			minWidth: 100, flex: 1,
			type: 'singleSelect',
			valueOptions: loanType.map(type => ({
				value: type.id,
				label: textTranslate(language)({ en: type.DescriptionEn, el: type.DescriptionEl }),
			})),
		},
		{
			field: 'RequestedAmount',
			headerName: t('Requested Amount'),
			minWidth: 150, flex: 1,
			type: 'number',
			renderCell: ({ row }) => {
				return row.RequestedAmount ? <NumericFormat
					thousandSeparator={thousandSeparator}
					decimalSeparator={decimalSeparator}
					displayType="text"
					value={row.RequestedAmount}
				/> : 0
			}
		},
		{
			field: 'ApprovedAmount',
			headerName: t('Approved amount (€)'),
			minWidth: 150, flex: 1,
			type: 'number',
			renderCell: ({ row }) => {
				return row.ApprovedAmount ? <NumericFormat
					thousandSeparator={thousandSeparator}
					decimalSeparator={decimalSeparator}
					displayType="text"
					value={row.ApprovedAmount}
				/> : 0
			}
		},
		{
			field: 'RealEstateValue',
			headerName: t('Property Commercial Value (€)'),
			minWidth: 180, flex: 1, type: 'number',
			renderCell: ({ row }) => {
				return row.RealEstateValue ? <NumericFormat
					thousandSeparator={thousandSeparator}
					decimalSeparator={decimalSeparator}
					displayType="text"
					value={row.RealEstateValue}
				/> : 0
			}
		},
		{
			field: 'CreatedAt',
			headerName: t('Created'),
			minWidth: 150, flex: 1, type: 'date',
			valueFormatter: params =>
				moment(params?.value).format("DD-MM-YYYY"),
			aggregable: false,
		},

	], [data, refreshGrid])

	return (
		<Box>


			<Grid container alignItems="center" justifyContent="space-between" 
				sx={{ margin:{xs:'18px 0 10px 0', sm:'40px 0 10px 0', md:'40px 0 10px 0'}}}
			>
				<Grid item xs={7} sm={7} md={6} lg={6}>
					<Typography className='title-xsm color-default' sx={{ marginLeft:'12px', fontSize:{xs:'18px !important',sm:'22px !important'}  }} >
						{t('My Loans')}
					</Typography>
				</Grid>
				<Grid item>
					<PermissionHOC requiredPermissions={['access_loan_create']}>
						<Button
							size={"small"}
							variant={"contained"}
							color={'info'}
							sx={{ textAlign: 'center', padding: { xs: '12px' }, minWidth: { xs: 'inherit' } }}
							onClick={() => {
								if (hasActiveLoans) {
									showPopup({
										title: t("Loan Request"),
										message: t(
										"You already have an active loan. Are you sure you want to proceed with creating a new one?",
										),
										cancel: t("No"),
										ok: t("Yes"),
										onConfirm: () => {
											history.push(getRouteUrl(ROUTE_PAGE_WIZARD));
										},
										cancelClassName: 'btn-popup-secondary',
									});
								} else {
									history.push(getRouteUrl(ROUTE_PAGE_WIZARD));
								}
							}}
						>
							<PlusIcon />  <Box component="span" sx={{ marginLeft: '10px', display: { xs: 'none', sm: 'inline' } }}>{t('Loan Request')}</Box>
						</Button>
					</PermissionHOC>
				</Grid>
			</Grid>


			<AlertMessageForSimpleUser />

			<PermissionHOC requiredPermissions={['access_loan_index_data']}>
				<DataGridWrapper>
					<DataGridPremium
						sx={{
							bgcolor: 'background.paper', ".MuiDataGrid-aggregationColumnHeaderLabel": {
								display: "none"
							},
							maxHeight: 650
						}}
						columns={columns}
						density={'compact'}
						rows={data}
						pagination
						paginationModel={{ page, pageSize }}
						paginationMode='server'
						onPaginationModelChange={onPaginationModelChange}
						pageSizeOptions={[5, 10, 15, 20, 50, 100]}
						rowCount={Number(total)}
						sortingMode="server"
						onSortModelChange={(sortModel) => onSortModelChange(sortModel)}
						filterMode='server'
						onFilterModelChange={onFilterModelChange}
						loading={isLoading}
						localeText={muiGridLocales(t)}
						slots={{
							pagination: CustomPagination,
						}}
						disableAggregation={true}
						disableRowGrouping={true}
					/>
				</DataGridWrapper>
			</PermissionHOC>
			{data.length > 0 ?
				<PermissionHOC requiredPermissions={['access_loan_index_list']}>
					<Box>
						<IndexListLoans data={data} />
					</Box>
				</PermissionHOC>
				:
				<PermissionHOC requiredPermissions={['access_loan_index_list']}>
					<Card sx={{ mt: { xs: '20px', sm: 0 }   }}>
						<CardContent>
							<Typography
								style={{ textAlign: 'center'}}>{t('No loan request has been registered yet.')}</Typography>
						</CardContent>
					</Card>
				</PermissionHOC>

			}

		</Box>
	);
};

export default withPermissionHOC(Index, ['access_loan_index'])